import React from 'react';
import { AuthProvider, useAuth } from '../shared/context/AuthContext';
import Login from '../shared/components/Login';
import { Loader2 } from 'lucide-react';

import DashboardLayout from '../shared/components/Dashboard/DashboardLayout';
import JobsFilterView from '../shared/components/Dashboard/JobsFilterView';
import SavedFiltersView from '../shared/components/Dashboard/SavedFiltersView';
import NotificationsView from '../shared/components/Dashboard/NotificationsView';
import ProfileSettings from '../shared/components/Dashboard/ProfileSettings';
import { useState, useEffect, useRef } from 'react';

function getPathInfo() {
    const path = window.location.pathname.replace(/^\/|\/$/g, '');
    const segments = path.split('/');
    return {
        view: segments[0] || 'jobs',
        id: segments[1] || null
    };
}

function AppContent() {
    const { loading, user } = useAuth();

    const initialRoute = getPathInfo();
    const [activeView, setActiveView] = useState(initialRoute.view);
    const [activeFilterId, setActiveFilterId] = useState(initialRoute.id);
    const historyRef = useRef([]);

    useEffect(() => {
        const onPopState = () => {
            const { view, id } = getPathInfo();
            setActiveView(view);
            setActiveFilterId(id);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const handleViewChange = (view, filterId = null) => {
        if (view === activeView && filterId === activeFilterId) return;

        if (view === 'jobs') {
            setActiveFilterId(filterId);
        } else {
            setActiveFilterId(null);
        }

        historyRef.current = [...historyRef.current, activeView];

        const url = new URL(window.location);
        url.search = '';

        let newPath = `/${view}`;
        if (view === 'jobs' && filterId) {
            newPath += `/${filterId}`;
        }

        url.pathname = newPath;
        window.history.pushState({}, '', url);

        setActiveView(view);
    };

    const handleBack = () => {
        if (historyRef.current.length === 0) {
            setActiveView('jobs');
            return;
        }
        const prevView = historyRef.current[historyRef.current.length - 1];
        historyRef.current = historyRef.current.slice(0, -1);
        setActiveView(prevView);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Login />;
    }

    return (
        <DashboardLayout
            activeView={activeView}
            onViewChange={handleViewChange}
        >
            {activeView === 'jobs' && <JobsFilterView onViewChange={handleViewChange} activeFilterId={activeFilterId} />}
            {activeView === 'saved-filters' && <SavedFiltersView onViewChange={handleViewChange} />}
            {activeView === 'settings' && <ProfileSettings />}
            {activeView === 'notifications' && <NotificationsView />}
        </DashboardLayout>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;
